import { POSTAL_CODE_FORMAT } from "@shared/formats/postal-code";
import type { RfqCreateArgs } from "@/components/rfq/types";
import { HEIGHT_LABELS, specFromParams } from "@/lib/listings/builder-state";

type Params = Record<string, string | string[] | undefined>;

function first(params: Params, key: string): string | undefined {
  const value = params[key];
  return Array.isArray(value) ? value[0] : value;
}

function footprintDimensions(footprint: string | null): RfqCreateArgs["dimensions"] {
  const match = footprint ? /^(\d+)x(\d+)$/.exec(footprint) : null;
  return match ? { length: Number(match[1]), width: Number(match[2]), unit: "in" } : undefined;
}

function productSpecsFor(spec: ReturnType<typeof specFromParams>): RfqCreateArgs["productSpecs"] {
  const entries = Object.entries(spec.specs);
  if (spec.walls !== null) entries.push(["wall", `${spec.walls}-wall`]);
  if (spec.heightBucket) entries.push(["heightRange", HEIGHT_LABELS[spec.heightBucket]]);
  if (spec.conditions.length > 1) entries.push(["alternateGrades", spec.conditions.join(", ")]);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function builderHandoffFromParams(params: Params): Partial<RfqCreateArgs> | undefined {
  const spec = specFromParams(params);
  const values: Partial<RfqCreateArgs> = {
    condition: spec.conditions[0],
    dimensions: footprintDimensions(spec.footprint),
    productSpecs: productSpecsFor(spec),
    notes: first(params, "notes")?.trim() || undefined,
  };
  const entries = Object.entries(values).filter(([, value]) => value !== undefined);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function postalCodeFromParams(params: Params): string | undefined {
  const raw = first(params, "zip")?.trim();
  const normalized = POSTAL_CODE_FORMAT.normalize(raw);
  if (!raw || !normalized) return undefined;
  const isWholeCode =
    /^\d{5}(-\d{4})?$/.test(raw) || POSTAL_CODE_FORMAT.caPattern.test(normalized);
  return isWholeCode ? normalized : undefined;
}
